import dayjs from "dayjs";
import type { ReactNode } from "react";

export interface OpenPositionItem {
    id: string;
    contactLabel: ReactNode;
    amountCents: number;
    date: Date;
    side: "debitor" | "kreditor";
}

export type OpenPositionBucketKey = "ueberfaellig" | "heute" | "woche" | "spaeter";

export interface OpenPositionSide {
    count: number;
    sumCents: number; // signed sum of item.amountCents - preserves credit-note netting
    items: OpenPositionItem[]; // capped at topN, most-urgent first
}

export interface OpenPositionBucket {
    key: OpenPositionBucketKey;
    debitor: OpenPositionSide;
    kreditor: OpenPositionSide;
}

const ORDER: OpenPositionBucketKey[] = ["ueberfaellig", "heute", "woche", "spaeter"];

const bucketOf = (date: Date, today: Date): OpenPositionBucketKey => {
    const d = dayjs(date).startOf("day");
    const t = dayjs(today).startOf("day");
    if (d.isBefore(t)) {
        return "ueberfaellig";
    }
    if (d.isSame(t)) {
        return "heute";
    }
    if (d.diff(t, "day") <= 7) {
        return "woche";
    }
    return "spaeter";
};

const buildSide = (items: OpenPositionItem[], topN: number): OpenPositionSide => {
    const sorted = items.slice().toSorted((a, b) => a.date.getTime() - b.date.getTime());
    return {
        count: sorted.length,
        sumCents: sorted.reduce((s, x) => s + x.amountCents, 0),
        items: sorted.slice(0, topN),
    };
};

export const bucketOpenPositions = (items: OpenPositionItem[], today: Date, topN: number): OpenPositionBucket[] => {
    const map = new Map<OpenPositionBucketKey, { debitor: OpenPositionItem[]; kreditor: OpenPositionItem[] }>();
    for (const it of items) {
        const k = bucketOf(it.date, today);
        const bucket = map.get(k) ?? map.set(k, { debitor: [], kreditor: [] }).get(k);
        bucket[it.side].push(it);
    }
    return ORDER.filter((k) => {
        const bucket = map.get(k);
        return !!bucket && bucket.debitor.length + bucket.kreditor.length > 0;
    }).map((k) => {
        const bucket = map.get(k);
        return {
            key: k,
            debitor: buildSide(bucket.debitor, topN),
            kreditor: buildSide(bucket.kreditor, topN),
        };
    });
};
